import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  createProjectPackagingRecord,
  deleteProjectPackagingRecord,
  fetchProjectPackagingWorkspace,
  updateProjectPackagingRecord,
} from "../api";
import { getApiErrorMessage } from "../api/client";

const LEGACY_DEFAULT_PROJECT_ID = "main_project";
const PAIRED_VARIANTS = [
  { value: "acidified", label: "Asitli" },
  { value: "unacidified", label: "Asitsiz" },
];

function normalizePositiveInteger(value) {
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 1) return null;
  return parsed;
}

function formatDateLabel(timestamp) {
  if (!timestamp) return "-";
  return new Intl.DateTimeFormat("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(new Date(timestamp));
}

function formatSequenceLabel(packageNo, groupNo) {
  if (!packageNo || !groupNo) return "-";
  return `${packageNo}. grup ${groupNo}. paket`;
}

function formatPaftaName(name) {
  return String(name || "-").replace(/\.[^.\\/]+$/i, "");
}

function getPointDisplayName(point) {
  return point?.name || point?.id || "-";
}

function comparePointsByNumber(left, right) {
  const getParts = (point) => {
    const label = String(getPointDisplayName(point));
    const match = label.match(/\d+/);
    return {
      numberValue: match ? Number(match[0]) : Number.MAX_SAFE_INTEGER,
      label: label.toLocaleLowerCase("tr-TR"),
    };
  };

  const leftParts = getParts(left);
  const rightParts = getParts(right);
  if (leftParts.numberValue !== rightParts.numberValue) {
    return leftParts.numberValue - rightParts.numberValue;
  }
  return leftParts.label.localeCompare(rightParts.label, "tr", {
    numeric: true,
    sensitivity: "base",
  });
}

function hasFilledCustomMeasurements(point) {
  const custom = point?.customMeasurements;
  if (!custom || typeof custom !== "object") return false;
  return Object.values(custom).some((value) => {
    if (value == null) return false;
    if (typeof value === "string") return value.trim() !== "";
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "object") return Object.keys(value).length > 0;
    return true;
  });
}

function isPointCollected(point) {
  if (!point) return false;
  if (point.hasDataEntry) return true;
  if (String(point.status || "").toLocaleLowerCase("tr-TR") === "pozitif") return true;
  if (point.veriGirisiTimestamp) return true;
  if (["sicaklik", "atm", "spc", "tds", "sal", "ph"].some((key) => point[key] != null)) {
    return true;
  }
  if (String(point.notMetni || "").trim()) return true;
  if (hasFilledCustomMeasurements(point)) return true;
  return Array.isArray(point.photoDataUrls) && point.photoDataUrls.length > 0;
}

export default function PackagingWorkspacePage({
  title,
  onBack,
  projects = [],
  selectedProjectId,
  isOnline,
}) {
  const [workspaceProject, setWorkspaceProject] = useState(null);
  const [paftalar, setPaftalar] = useState([]);
  const [points, setPoints] = useState([]);
  const [records, setRecords] = useState([]);
  const [selectedPaftaId, setSelectedPaftaId] = useState("");
  const [selectedPointIds, setSelectedPointIds] = useState([]);
  const [selectedRecordId, setSelectedRecordId] = useState("");
  const [packageNoDraft, setPackageNoDraft] = useState("1");
  const [groupNoDraft, setGroupNoDraft] = useState("1");
  const [printTarget, setPrintTarget] = useState("draft");
  const [labelVariant, setLabelVariant] = useState("acidified");
  const [editingRecordId, setEditingRecordId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isMutating, setIsMutating] = useState(false);
  const [loadNotice, setLoadNotice] = useState("");
  const [saveNotice, setSaveNotice] = useState("");
  const [saveNoticeIsError, setSaveNoticeIsError] = useState(false);

  const selectedProject = useMemo(
    () => projects.find((project) => String(project.id) === String(selectedProjectId)) || null,
    [projects, selectedProjectId]
  );

  const selectedProjectExists = useMemo(
    () => projects.some((project) => String(project.id) === String(selectedProjectId)),
    [projects, selectedProjectId]
  );

  const shouldPromptForProject =
    !selectedProjectId ||
    (selectedProjectId === LEGACY_DEFAULT_PROJECT_ID && !selectedProjectExists);

  const selectedProjectLabel = selectedProject?.name || workspaceProject?.name || "Aktif proje";

  const selectedPafta = useMemo(
    () => paftalar.find((pafta) => pafta.id === selectedPaftaId) || null,
    [paftalar, selectedPaftaId]
  );

  const selectedPaftaRecords = useMemo(
    () =>
      records
        .filter((record) => record.paftaId === selectedPaftaId)
        .sort((left, right) => {
          if (left.packageNo !== right.packageNo) return right.packageNo - left.packageNo;
          if (left.groupNo !== right.groupNo) return right.groupNo - left.groupNo;
          return (right.createdAt || 0) - (left.createdAt || 0);
        }),
    [records, selectedPaftaId]
  );

  const latestRecord = selectedPaftaRecords[0] || null;
  const nextSequence = useMemo(() => {
    if (!latestRecord) return { packageNo: 1, groupNo: 1 };
    return {
      packageNo: Number(latestRecord.packageNo || 1),
      groupNo: Number(latestRecord.groupNo || 0) + 1,
    };
  }, [latestRecord]);

  const selectedPaftaPoints = useMemo(
    () => points.filter((point) => point.paftaId === selectedPaftaId).sort(comparePointsByNumber),
    [points, selectedPaftaId]
  );

  const packageablePaftaPoints = useMemo(
    () => selectedPaftaPoints.filter((point) => isPointCollected(point)),
    [selectedPaftaPoints]
  );

  const selectedPointIdSet = useMemo(() => new Set(selectedPointIds), [selectedPointIds]);

  const selectedPoints = useMemo(
    () =>
      selectedPaftaPoints
        .filter((point) => selectedPointIdSet.has(point.id))
        .sort(comparePointsByNumber),
    [selectedPaftaPoints, selectedPointIdSet]
  );

  const availablePaftaPoints = useMemo(
    () =>
      packageablePaftaPoints.filter((point) => {
        if (
          (point.packagingRecordId && point.packagingRecordId !== editingRecordId) ||
          selectedPointIdSet.has(point.id)
        ) {
          return false;
        }
        return true;
      }),
    [editingRecordId, packageablePaftaPoints, selectedPointIdSet]
  );

  const selectedRecord = useMemo(
    () => selectedPaftaRecords.find((record) => record.id === selectedRecordId) || latestRecord || null,
    [latestRecord, selectedPaftaRecords, selectedRecordId]
  );

  const selectedRecordPoints = useMemo(() => {
    if (!selectedRecord) return [];
    const recordPointIds = new Set(selectedRecord.pointIds || []);
    return selectedPaftaPoints.filter((point) => recordPointIds.has(point.id)).sort(comparePointsByNumber);
  }, [selectedPaftaPoints, selectedRecord]);

  const selectedCount = selectedPoints.length;
  const packagedCount = packageablePaftaPoints.filter((point) => point.packagingRecordId).length;
  const selectedNewPointCount = selectedPoints.filter((point) => !point.packagingRecordId).length;
  const remainingCount = Math.max(
    packageablePaftaPoints.length - packagedCount - selectedNewPointCount,
    0
  );
  const packageNo = normalizePositiveInteger(packageNoDraft) || 1;
  const groupNo = normalizePositiveInteger(groupNoDraft) || 1;
  const editingRecord = useMemo(
    () => selectedPaftaRecords.find((record) => record.id === editingRecordId) || null,
    [editingRecordId, selectedPaftaRecords]
  );

  const loadWorkspace = useCallback(async () => {
    if (!selectedProjectId || shouldPromptForProject) {
      setWorkspaceProject(null);
      setPaftalar([]);
      setPoints([]);
      setRecords([]);
      setSelectedPaftaId("");
      setSelectedPointIds([]);
      setSelectedRecordId("");
      setEditingRecordId("");
      setLoadNotice("Başlamak için aktif proje seçin.");
      setSaveNotice("");
      return;
    }

    setIsLoading(true);
    setLoadNotice("");

    try {
      const data = await fetchProjectPackagingWorkspace(selectedProjectId);
      const nextProject = data?.project || null;
      const nextPaftalar = Array.isArray(data?.paftalar)
        ? data.paftalar.filter((pafta) => pafta.aktif !== false)
        : [];
      const nextRecords = Array.isArray(data?.records) ? data.records : [];
      const nextPoints = Array.isArray(data?.points) ? data.points : [];

      setWorkspaceProject(nextProject);
      setPaftalar(nextPaftalar);
      setRecords(nextRecords);
      setPoints(nextPoints);
      setSelectedPaftaId((previous) => {
        if (nextPaftalar.some((pafta) => pafta.id === previous)) return previous;
        return nextPaftalar[0]?.id || "";
      });
      setSelectedPointIds([]);
      setSelectedRecordId("");
      setEditingRecordId("");
      setSaveNotice("");
      setSaveNoticeIsError(false);
    } catch (error) {
      setLoadNotice(getApiErrorMessage(error, "Paketleme verileri yüklenemedi."));
    } finally {
      setIsLoading(false);
    }
  }, [selectedProjectId, shouldPromptForProject]);

  useEffect(() => {
    loadWorkspace();
  }, [loadWorkspace]);

  useEffect(() => {
    if (editingRecordId) return;
    setPackageNoDraft(String(nextSequence.packageNo));
    setGroupNoDraft(String(nextSequence.groupNo));
  }, [editingRecordId, nextSequence.groupNo, nextSequence.packageNo, selectedPaftaId]);

  const handleAddPoint = useCallback((point) => {
    if (!point || (point.packagingRecordId && point.packagingRecordId !== editingRecordId)) return;
    setSelectedPointIds((previous) => {
      if (previous.includes(point.id)) return previous;
      return [...previous, point.id];
    });
    setSaveNotice("");
  }, [editingRecordId]);

  const handleRemovePoint = useCallback((pointId) => {
    setSelectedPointIds((previous) => previous.filter((id) => id !== pointId));
    setSaveNotice("");
  }, []);

  const handleAddAllFiltered = useCallback(() => {
    setSelectedPointIds((previous) => {
      const next = new Set(previous);
      availablePaftaPoints.forEach((point) => next.add(point.id));
      return Array.from(next);
    });
    setSaveNotice("");
  }, [availablePaftaPoints]);

  const handleCancelEdit = useCallback(() => {
    setEditingRecordId("");
    setSelectedPointIds([]);
    setPackageNoDraft(String(nextSequence.packageNo));
    setGroupNoDraft(String(nextSequence.groupNo));
    setSaveNotice("");
    setSaveNoticeIsError(false);
  }, [nextSequence.groupNo, nextSequence.packageNo]);

  const handleStartEdit = useCallback((record) => {
    if (!record) return;
    setEditingRecordId(record.id);
    setSelectedRecordId(record.id);
    setSelectedPointIds(record.pointIds || []);
    setPackageNoDraft(String(record.packageNo));
    setGroupNoDraft(String(record.groupNo));
    setSaveNotice("");
    setSaveNoticeIsError(false);
  }, []);

  const handleSaveRecord = useCallback(async () => {
    if (!selectedProjectId || !selectedPaftaId) {
      setSaveNotice("Önce pafta seçin.");
      setSaveNoticeIsError(true);
      return;
    }

    if (selectedPointIds.length === 0) {
      setSaveNotice("Pakete eklenecek en az 1 numune ekleyin.");
      setSaveNoticeIsError(true);
      return;
    }

    const nextPackageNo = normalizePositiveInteger(packageNoDraft);
    const nextGroupNo = normalizePositiveInteger(groupNoDraft);
    if (!nextPackageNo || !nextGroupNo) {
      setSaveNotice("Grup ve paket numarası en az 1 olmalıdır.");
      setSaveNoticeIsError(true);
      return;
    }

    setSaveNoticeIsError(false);
    setIsMutating(true);

    try {
      if (editingRecord) {
        const updated = await updateProjectPackagingRecord(
          selectedProjectId,
          editingRecord.id,
          {
            notes: editingRecord.notes || "",
            pointIds: selectedPointIds,
            baseUpdatedAt: editingRecord.updatedAt,
          }
        );
        const previousPointIdSet = new Set(editingRecord.pointIds || []);
        const nextPointIdSet = new Set(updated.pointIds || []);

        setRecords((previous) =>
          previous.map((record) => (record.id === updated.id ? updated : record))
        );
        setPoints((previous) =>
          previous.map((point) => {
            if (nextPointIdSet.has(point.id)) {
              return {
                ...point,
                packagingRecordId: updated.id,
                packagingPackageNo: updated.packageNo,
                packagingGroupNo: updated.groupNo,
                packagingAcidStatus: updated.acidStatus,
              };
            }
            if (previousPointIdSet.has(point.id)) {
              return {
                ...point,
                packagingRecordId: null,
                packagingPackageNo: null,
                packagingGroupNo: null,
                packagingAcidStatus: null,
              };
            }
            return point;
          })
        );
        setPaftalar((previous) =>
          previous.map((pafta) => {
            if (pafta.id !== selectedPaftaId) return pafta;
            return {
              ...pafta,
              packagedSampleCount:
                Number(pafta.packagedSampleCount || 0) -
                Number(editingRecord.sampleCount || 0) +
                Number(updated.sampleCount || 0),
              lastSampleCount:
                pafta.lastPackageNo === updated.packageNo &&
                pafta.lastGroupNo === updated.groupNo
                  ? updated.sampleCount
                  : pafta.lastSampleCount,
            };
          })
        );
        setEditingRecordId("");
        setSelectedPointIds([]);
        setSelectedRecordId(updated.id);
        setSaveNoticeIsError(false);
        setSaveNotice(`${formatSequenceLabel(updated.packageNo, updated.groupNo)} güncellendi.`);
      } else {
        const created = await createProjectPackagingRecord(selectedProjectId, {
          paftaId: selectedPaftaId,
          packageNo: nextPackageNo,
          groupNo: nextGroupNo,
          acidStatus: "mixed",
          notes: "",
          pointIds: selectedPointIds,
        });

        setRecords((previous) => [created, ...previous]);
        setPoints((previous) =>
          previous.map((point) => {
            if (!selectedPointIds.includes(point.id)) return point;
            return {
              ...point,
              packagingRecordId: created.id,
              packagingPackageNo: created.packageNo,
              packagingGroupNo: created.groupNo,
              packagingAcidStatus: created.acidStatus,
            };
          })
        );
        setPaftalar((previous) =>
          previous.map((pafta) => {
            if (pafta.id !== selectedPaftaId) return pafta;
            return {
              ...pafta,
              packagedSampleCount: Number(pafta.packagedSampleCount || 0) + created.sampleCount,
              packagingRecordCount: Number(pafta.packagingRecordCount || 0) + 1,
              lastPackageNo: created.packageNo,
              lastGroupNo: created.groupNo,
              lastSampleCount: created.sampleCount,
              lastAcidStatus: created.acidStatus,
            };
          })
        );

        setPackageNoDraft(String(created.packageNo));
        setGroupNoDraft(String(Number(created.groupNo || 0) + 1));
        setSelectedPointIds([]);
        setSelectedRecordId(created.id);
        setSaveNoticeIsError(false);
        setSaveNotice(`${formatSequenceLabel(created.packageNo, created.groupNo)} kaydedildi.`);
      }
    } catch (error) {
      const errorMessage = getApiErrorMessage(
        error,
        editingRecord ? "Paket kaydı güncellenemedi." : "Paketleme kaydı oluşturulamadı."
      );
      await loadWorkspace();
      setSaveNoticeIsError(true);
      setSaveNotice(errorMessage);
    } finally {
      setIsMutating(false);
    }
  }, [
    editingRecord,
    groupNoDraft,
    loadWorkspace,
    packageNoDraft,
    selectedPaftaId,
    selectedPointIds,
    selectedProjectId,
  ]);

  const handleDeleteRecord = useCallback(
    async (record) => {
      if (!record || !selectedProjectId) return;
      const confirmed = window.confirm(
        `${formatSequenceLabel(record.packageNo, record.groupNo)} kaydını silmek istediğinize emin misiniz?`
      );
      if (!confirmed) return;

      setIsMutating(true);
      setSaveNotice("");
      setSaveNoticeIsError(false);
      try {
        await deleteProjectPackagingRecord(selectedProjectId, record.id, record.updatedAt);
        if (editingRecordId === record.id) {
          setEditingRecordId("");
          setSelectedPointIds([]);
        }
        await loadWorkspace();
        setSaveNoticeIsError(false);
        setSaveNotice(`${formatSequenceLabel(record.packageNo, record.groupNo)} silindi.`);
      } catch (error) {
        setSaveNoticeIsError(true);
        setSaveNotice(getApiErrorMessage(error, "Paket kaydı silinemedi."));
      } finally {
        setIsMutating(false);
      }
    },
    [editingRecordId, loadWorkspace, selectedProjectId]
  );

  const handlePrintLabel = useCallback((target, recordId = null) => {
    setPrintTarget(target);
    if (recordId) setSelectedRecordId(recordId);
    window.setTimeout(() => window.print(), 80);
  }, []);

  const selectedLabelVariant =
    PAIRED_VARIANTS.find((variant) => variant.value === labelVariant) || PAIRED_VARIANTS[0];

  const renderLabel = (labelInfo, target) => (
    <div
      className="packaging-label packaging-label--print-source"
      id={printTarget === target ? "packaging-print-label" : undefined}
    >
      <div className="packaging-label__top">
        <div>
          <div className="packaging-label__eyebrow">Grup / Paket</div>
          <h2>{formatSequenceLabel(labelInfo.packageNo, labelInfo.groupNo)}</h2>
        </div>
        <div className="packaging-label__sequence">{selectedLabelVariant.label}</div>
      </div>

      <div className="packaging-label__grid">
        <div>
          <span>Toplam Eklenen Numune</span>
          <strong>{labelInfo.sampleCount}</strong>
        </div>
      </div>

      <div className="packaging-label__sample-list">
        {labelInfo.points.map((point) => (
          <div key={point.id} className="packaging-label__sample-row">
            <strong>{getPointDisplayName(point)}</strong>
          </div>
        ))}
      </div>

      <div className="packaging-label__footer">
        <span>{labelInfo.createdAt ? formatDateLabel(labelInfo.createdAt) : "Taslak"}</span>
      </div>
    </div>
  );

  const selectedPaftaSummary = selectedPafta
    ? `${packageablePaftaPoints.length} veri girişli numune, ${remainingCount} bekleyen`
    : "Pafta seçin";

  const draftLabelInfo = {
    packageNo,
    groupNo,
    sampleCount: selectedPoints.length,
    points: selectedPoints,
    createdAt: null,
    createdByUsername: "",
  };

  const savedLabelInfo = selectedRecord
    ? {
        paftaName: selectedRecord.paftaName,
        packageNo: selectedRecord.packageNo,
        groupNo: selectedRecord.groupNo,
        sampleCount: selectedRecord.sampleCount,
        points: selectedRecordPoints,
        createdAt: selectedRecord.createdAt,
      }
    : null;

  const visibleLabelInfo = selectedPoints.length > 0 ? draftLabelInfo : savedLabelInfo;
  const visibleLabelTarget = selectedPoints.length > 0 ? "draft" : "saved";

  return (
    <div className="tool-workspace packaging-workspace">
      <div className="tool-workspace__panel packaging-workspace__panel">
        <div className="tool-workspace__header">
          <h1 className="tool-workspace__title">{title}</h1>

          <button type="button" className="tool-workspace__back-button" onClick={onBack}>
            <span aria-hidden="true">←</span>
            Haritaya Dön
          </button>
        </div>

        <section className="packaging-page__project-card">
          <div>
            <div className="packaging-page__eyebrow">Aktif Proje</div>
            <h2 className="packaging-page__title">{selectedProjectLabel}</h2>
          </div>
          <div className="packaging-page__project-meta">
            <div className="packaging-page__metric">
              <span>Pafta</span>
              <strong>{formatPaftaName(selectedPafta?.isim)}</strong>
            </div>
            <div className="packaging-page__metric">
              <span>Sıradaki</span>
              <strong>{formatSequenceLabel(nextSequence.packageNo, nextSequence.groupNo)}</strong>
            </div>
            <div className="packaging-page__metric">
              <span>Seçilen</span>
              <strong>{selectedCount}</strong>
            </div>
          </div>
        </section>

        <div className="packaging-page packaging-page--command">
          <aside className="packaging-page__sidebar">
            <div className="packaging-page__sidebar-header">
              <h2 className="packaging-page__section-title">Paftalar</h2>
              <span className="packaging-page__badge">{paftalar.length}</span>
            </div>

            {paftalar.length > 0 ? (
              <div className="packaging-page__pafta-list">
                <div className="packaging-page__matrix-header packaging-page__matrix-header--pafta" aria-hidden="true">
                  <span>Pafta</span>
                  <span>Numune</span>
                </div>
                {paftalar.map((pafta) => {
                  const isActive = pafta.id === selectedPaftaId;
                  const total = Number(pafta.sampleCount || 0);
                  const done = Number(pafta.packagedSampleCount || 0);
                  return (
                    <button
                      key={pafta.id}
                      type="button"
                      className={`packaging-page__pafta-card ${
                        isActive ? "packaging-page__pafta-card--active" : ""
                      }`.trim()}
                      onClick={() => {
                        setSelectedPaftaId(pafta.id);
                        setSelectedPointIds([]);
                        setSelectedRecordId("");
                        setEditingRecordId("");
                        setSaveNotice("");
                        setSaveNoticeIsError(false);
                      }}
                    >
                      <div className="packaging-page__pafta-name">{formatPaftaName(pafta.isim)}</div>
                      <div className="packaging-page__pafta-meta">{done}/{total}</div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="packaging-page__empty-state">Bu proje için aktif pafta yok.</div>
            )}
          </aside>

          <section className="packaging-page__content">
            {shouldPromptForProject ? (
              <div className="packaging-page__empty-state packaging-page__empty-state--content">
                Paketleme için aktif proje seçin.
              </div>
            ) : selectedPafta ? (
              <>
                <div className="packaging-page__content-header">
                  <div>
                    <div className="packaging-page__eyebrow">
                      {editingRecord ? "Paket Düzenleme" : "Numune Seçimi"}
                    </div>
                    <h2 className="packaging-page__content-title">{formatPaftaName(selectedPafta.isim)}</h2>
                  </div>
                  <div className="packaging-page__content-note">{selectedPaftaSummary}</div>
                </div>

                <div className="packaging-page__form-grid packaging-page__form-grid--compact">
                  <div className="packaging-page__field">
                    <label className="packaging-page__label" htmlFor="packaging-group-no">
                      Grup No
                    </label>
                    <input
                      id="packaging-group-no"
                      type="number"
                      min="1"
                      className="packaging-page__input"
                      value={packageNoDraft}
                      onChange={(event) => {
                        setPackageNoDraft(event.target.value);
                        setSaveNotice("");
                      }}
                      disabled={isLoading || isMutating || !isOnline || Boolean(editingRecord)}
                    />
                  </div>

                  <div className="packaging-page__field">
                    <label className="packaging-page__label" htmlFor="packaging-package-no">
                      Paket No
                    </label>
                    <input
                      id="packaging-package-no"
                      type="number"
                      min="1"
                      className="packaging-page__input"
                      value={groupNoDraft}
                      onChange={(event) => {
                        setGroupNoDraft(event.target.value);
                        setSaveNotice("");
                      }}
                      disabled={isLoading || isMutating || !isOnline || Boolean(editingRecord)}
                    />
                  </div>

                </div>

                <div className="packaging-page__transfer-board">
                  <div className="packaging-page__transfer-column packaging-page__transfer-column--available">
                    <div className="packaging-page__records-header">
                      <h3 className="packaging-page__section-title">Alınmış Numuneler</h3>
                      <span className="packaging-page__badge">{availablePaftaPoints.length}</span>
                    </div>
                    <div className="packaging-page__point-list">
                      <div className="packaging-page__matrix-header packaging-page__matrix-header--points" aria-hidden="true">
                        <span>Numune</span>
                        <span>İşlem</span>
                      </div>
                      {availablePaftaPoints.length > 0 ? (
                        availablePaftaPoints.map((point) => (
                          <div key={point.id} className="packaging-page__point-row packaging-page__point-row--compact">
                            <strong>{getPointDisplayName(point)}</strong>
                            <button
                              type="button"
                              className="packaging-page__inline-action"
                              onClick={() => handleAddPoint(point)}
                              disabled={isMutating}
                              aria-label={`${getPointDisplayName(point)} numunesini pakete ekle`}
                              title="Pakete ekle"
                            >
                              Ekle
                            </button>
                          </div>
                        ))
                      ) : (
                        <div className="packaging-page__empty-state">
                          Bu paftada eklenebilir veri girişli numune yok.
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="packaging-page__transfer-column packaging-page__transfer-column--selected">
                    <div className="packaging-page__records-header">
                      <h3 className="packaging-page__section-title">Pakettekiler</h3>
                      <span className="packaging-page__badge">{selectedCount}</span>
                    </div>
                    <div className="packaging-page__point-list">
                      <div className="packaging-page__matrix-header packaging-page__matrix-header--points" aria-hidden="true">
                        <span>Numune</span>
                        <span>İşlem</span>
                      </div>
                      {selectedPoints.length > 0 ? (
                        selectedPoints.map((point) => (
                          <div key={point.id} className="packaging-page__point-row packaging-page__point-row--compact packaging-page__point-row--selected">
                            <strong>{getPointDisplayName(point)}</strong>
                            <button
                              type="button"
                              className="packaging-page__inline-action packaging-page__inline-action--danger"
                              onClick={() => handleRemovePoint(point.id)}
                              disabled={isMutating}
                              aria-label={`${getPointDisplayName(point)} numunesini paketten kaldır`}
                              title="Paketten kaldır"
                            >
                              Kaldır
                            </button>
                          </div>
                        ))
                      ) : (
                        <div className="packaging-page__empty-state">Pakete numune ekleyin.</div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="packaging-page__actions packaging-page__actions--row">
                  <button
                    type="button"
                    className="packaging-page__secondary-button"
                    onClick={handleAddAllFiltered}
                    disabled={availablePaftaPoints.length === 0 || isMutating}
                  >
                    Görünenleri Ekle
                  </button>
                  <button
                    type="button"
                    className="packaging-page__secondary-button"
                    onClick={() => setSelectedPointIds([])}
                    disabled={selectedPointIds.length === 0 || isMutating}
                  >
                    Seçimi Temizle
                  </button>
                  {editingRecord ? (
                    <button
                      type="button"
                      className="packaging-page__secondary-button"
                      onClick={handleCancelEdit}
                      disabled={isMutating}
                    >
                      Düzenlemeyi İptal Et
                    </button>
                  ) : null}
                </div>
              </>
            ) : (
              <div className="packaging-page__empty-state packaging-page__empty-state--content">
                {loadNotice || "Pafta seçin."}
              </div>
            )}

            {loadNotice && !shouldPromptForProject ? (
              <div className="packaging-page__helper-text packaging-page__helper-text--notice">
                {loadNotice}
              </div>
            ) : null}
          </section>

          <aside className="packaging-page__history">
            <div className="packaging-page__records-header">
              <div>
                <div className="packaging-page__eyebrow">
                  {editingRecord ? "Düzenleniyor" : "Yeni Paket"}
                </div>
                <h3 className="packaging-page__section-title">
                  {formatSequenceLabel(packageNo, groupNo)}
                </h3>
              </div>
              <span className="packaging-page__badge">{selectedCount}</span>
            </div>

            <div className="packaging-page__package-actions">
              <div className="packaging-page__variant-toggle" aria-label="Etiket türü">
                {PAIRED_VARIANTS.map((variant) => (
                  <button
                    key={variant.value}
                    type="button"
                    className={`packaging-page__variant-button ${
                      labelVariant === variant.value ? "packaging-page__variant-button--active" : ""
                    }`.trim()}
                    onClick={() => setLabelVariant(variant.value)}
                  >
                    {variant.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                className="packaging-page__primary-button"
                onClick={handleSaveRecord}
                disabled={
                  isLoading ||
                  isMutating ||
                  !isOnline ||
                  !selectedPaftaId ||
                  selectedCount === 0
                }
              >
                {editingRecord ? "Değişiklikleri Kaydet" : "Paketi Kaydet"}
              </button>
              <button
                type="button"
                className="packaging-page__secondary-button"
                onClick={() => handlePrintLabel(visibleLabelTarget)}
                disabled={!visibleLabelInfo || visibleLabelInfo.sampleCount === 0 || isMutating}
              >
                Etiketi Yazdır
              </button>
              {saveNotice ? (
                <div
                  className={`packaging-page__helper-text packaging-page__helper-text--status ${
                    saveNoticeIsError ? "packaging-page__helper-text--error" : ""
                  }`.trim()}
                  role={saveNoticeIsError ? "alert" : "status"}
                >
                  {saveNotice}
                </div>
              ) : null}
            </div>

            <div className="packaging-page__records-section">
              <div className="packaging-page__records-header">
                <h3 className="packaging-page__section-title">Kayıtlı Paketler</h3>
                <span className="packaging-page__badge">{selectedPaftaRecords.length}</span>
              </div>

              <div className="packaging-page__record-list">
                <div className="packaging-page__record-list-header" aria-hidden="true">
                  <span>Grup</span>
                  <span>Paket</span>
                </div>
                {selectedPaftaRecords.length > 0 ? (
                  selectedPaftaRecords.map((record) => (
                    <button
                      key={record.id}
                      type="button"
                      className={`packaging-page__record-card ${
                        selectedRecord?.id === record.id ? "packaging-page__record-card--active" : ""
                      }`.trim()}
                      disabled={isMutating || Boolean(editingRecordId)}
                      onClick={() => {
                        setSelectedRecordId(record.id);
                        setSelectedPointIds([]);
                      }}
                    >
                      <strong>{record.packageNo}</strong>
                      <strong>{record.groupNo}</strong>
                    </button>
                  ))
                ) : (
                  <div className="packaging-page__empty-state">Bu paftada kayıt yok.</div>
                )}
              </div>
            </div>
          </aside>

          <aside className="packaging-page__viewer">
            {selectedRecord ? (
              <div className="packaging-page__record-detail">
                <div className="packaging-page__records-header">
                  <div>
                    <div className="packaging-page__eyebrow">Paket İçeriği</div>
                    <h3 className="packaging-page__section-title">
                      {formatSequenceLabel(selectedRecord.packageNo, selectedRecord.groupNo)}
                    </h3>
                  </div>
                  <span className="packaging-page__badge">{selectedRecord.sampleCount}</span>
                </div>
                <div className="packaging-page__record-meta">
                  {formatDateLabel(selectedRecord.createdAt)}
                </div>
                <div className="packaging-page__record-points">
                  <div className="packaging-page__matrix-header packaging-page__matrix-header--record-points" aria-hidden="true">
                    <span>No</span>
                    <span>Numune</span>
                  </div>
                  {selectedRecordPoints.map((point, index) => (
                    <div key={point.id} className="packaging-page__record-point">
                      <span>{index + 1}</span>
                      <strong>{getPointDisplayName(point)}</strong>
                    </div>
                  ))}
                </div>
                <div className="packaging-page__actions packaging-page__actions--row">
                  <button
                    type="button"
                    className="packaging-page__secondary-button"
                    onClick={() => handleStartEdit(selectedRecord)}
                    disabled={isMutating}
                  >
                    Düzenle
                  </button>
                  <button
                    type="button"
                    className="packaging-page__secondary-button packaging-page__secondary-button--danger"
                    onClick={() => handleDeleteRecord(selectedRecord)}
                    disabled={isMutating}
                  >
                    Sil
                  </button>
                  <button
                    type="button"
                    className="packaging-page__secondary-button"
                    onClick={() => handlePrintLabel("saved", selectedRecord.id)}
                    disabled={isMutating}
                  >
                    Yazdır
                  </button>
                </div>
              </div>
            ) : (
              <div className="packaging-page__empty-state packaging-page__empty-state--content">
                Görüntülemek için kayıtlı bir paket seçin.
              </div>
            )}
          </aside>
        </div>

        {selectedPoints.length > 0 ? renderLabel(draftLabelInfo, "draft") : null}
        {savedLabelInfo ? renderLabel(savedLabelInfo, "saved") : null}
      </div>
    </div>
  );
}
